import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  mapSkeletonLabelRecipe,
  mapSkeletonRecipe,
} from '../recipes/map.recipe';
import { Text } from './Text';

export interface MapSkeletonProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** What is loading, in words: "Loading the route for this call". */
  label: string;
}

/**
 * A map drawn empty while what goes on it loads: a few streets, a route
 * across them with an end at each side, and a line saying what the wait is
 * for. It covers the real map, so a half-drawn one is never seen.
 *
 *   {loading && (
 *     <MapSkeleton label="Loading the route for this call"
 *       className="absolute inset-0 z-10" />
 *   )}
 *
 * Deliberately still, like every skeleton here: no pulse, no shimmer, no
 * line that draws itself.
 */
export const MapSkeleton = React.forwardRef<HTMLDivElement, MapSkeletonProps>(
  function MapSkeleton({ label, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        role="status"
        className={cn(mapSkeletonRecipe(), className)}
        {...props}
      >
        <svg
          viewBox="0 0 800 500"
          preserveAspectRatio="xMidYMid slice"
          className="absolute inset-0 size-full"
          aria-hidden="true"
          focusable="false"
        >
          <g className="fill-none stroke-background" strokeLinecap="round">
            <path d="M-20 120 H820" strokeWidth="10" />
            <path d="M-20 330 C200 300 420 380 820 340" strokeWidth="14" />
            <path d="M160 -20 V520" strokeWidth="8" />
            <path d="M470 -20 C440 160 520 300 500 520" strokeWidth="12" />
            <path d="M650 -20 V520" strokeWidth="6" />
            <path d="M-20 450 L300 220 L560 60 L820 -10" strokeWidth="6" />
          </g>
          <path
            d="M160 120 H300 C360 120 380 180 380 230 C380 300 440 340 500 340 H650"
            className="fill-none stroke-muted-foreground/30"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle
            cx="160"
            cy="120"
            r="11"
            className="fill-background stroke-muted-foreground/40"
            strokeWidth="4"
          />
          <circle
            cx="650"
            cy="340"
            r="11"
            className="fill-muted-foreground/40"
          />
        </svg>
        <span className={mapSkeletonLabelRecipe()}>
          <Text as="span" variant="body-sm-strong" tone="muted">
            {label}
          </Text>
        </span>
      </div>
    );
  }
);
